import { useState, useMemo } from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { useClusterByZone } from '../../assets/api/useAdminApi';
import useZoneStore from '../../store/useZoneStore';
import MainDialogBox from '../../shared/components/Dialog/MainDialogBox';
import { useDialog } from '../../shared/hook/useDialog';
import DataGridSkeleton from '../../shared/components/DataGridSkeleton';
import CustomPagination from '../../shared/utils/CustomPagination';

export const useZoneColumnsWithDialog = () => {
  const [selectedRow, setSelectedRow] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const { open, handleClickOpen, handleClose } = useDialog();
  const { setZone } = useZoneStore();

  const { data: clusterList, isLoading: isClusterLoading } = useClusterByZone({
    zone_id: selectedRow?.id,
  });

  const clusterRows = useMemo(() => {
    return clusterList?.map((item, index) => ({
      ...item,
      sr_no: index + 1,
    })) || [];
  }, [clusterList]);

  const viewZone = (row) => {
    setSelectedRow(row);
    handleClickOpen();
  };

  const zoneColumns = [
    {
      field: 'sr_no',
      headerName: 'Sr No',
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'zone_name',
      headerName: 'Zone Name',
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'city_name',
      headerName: 'City Name',
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'state_name',
      headerName: 'State',
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'country_name',
      headerName: 'Country',
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="Edit">
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => {
                const selected = params.row;
                setZone(selected);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </Tooltip>
        </Box>
      ),
    },
  ];

  const dialogJSX = (
    <MainDialogBox open={open} handleClose={handleClose} width="60%">
      {isClusterLoading ? (
        <DataGridSkeleton rowsNum={5} />
      ) : (
        <DataGrid
          rows={clusterRows}
          columns={[
            {
              field: 'sr_no',
              headerName: 'Sr No',
              flex: 1,
              renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
            },
            {
              field: 'cluster_name',
              headerName: 'Cluster Name',
              flex: 1,
              renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
            },
          ]}
          getRowId={(row) => row.id}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5]}
          slots={{
            pagination: () => (
              <CustomPagination
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                totalRows={clusterRows?.length || 0}
                pageSize={paginationModel.pageSize}
              />
            ),
          }}
        />
      )}
    </MainDialogBox>
  );

  return { zoneColumns, viewZone, dialogJSX };
};
